import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOffline } from '@/contexts/OfflineContext';
import { createIncident } from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  AlertTriangle, ChevronRight, ChevronLeft, MapPin, Calendar,
  Camera, CheckCircle, FileWarning, Send, X, Smartphone, Shield,
  WifiOff, Cloud, CloudOff
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import PhotoCapture from '@/components/PhotoCapture';

const QUICK_CATEGORIES = [
  { value: 'AIRPROX', label: 'Near Miss / Airprox', icon: '✈️' },
  { value: 'BIRD', label: 'Bird Strike', icon: '🐦' },
  { value: 'GCOL', label: 'Ground Collision', icon: '🚧' },
  { value: 'RE', label: 'Runway Excursion', icon: '🛬' },
  { value: 'RI', label: 'Runway Incursion', icon: '⚠️' },
  { value: 'TURB', label: 'Turbulence Event', icon: '🌪️' },
  { value: 'FUEL', label: 'Fuel Issue', icon: '⛽' },
  { value: 'MED', label: 'Medical Emergency', icon: '🏥' },
  { value: 'SEC', label: 'Security Event', icon: '🔒' },
  { value: 'OTHR', label: 'Other Hazard', icon: '📋' },
];

const SEVERITY_OPTIONS = [
  { value: 'negligible', label: 'Negligible', description: 'No safety impact', color: 'bg-slate-600' },
  { value: 'minor', label: 'Minor', description: 'Slight impact, easily managed', color: 'bg-blue-600' },
  { value: 'major', label: 'Major', description: 'Significant safety concern', color: 'bg-yellow-600' },
  { value: 'hazardous', label: 'Hazardous', description: 'Serious safety threat', color: 'bg-orange-600' },
  { value: 'catastrophic', label: 'Catastrophic', description: 'Life-threatening', color: 'bg-red-600' },
];

const STEPS = [
  { id: 1, title: 'Category', description: 'What type of event?' },
  { id: 2, title: 'Details', description: 'Describe what happened' },
  { id: 3, title: 'Severity', description: 'How serious was it?' },
  { id: 4, title: 'Review', description: 'Confirm and submit' },
];

const REPORTING_MODES = [
  { value: 'normal', label: 'Normal', description: 'Your identity is visible', icon: '👤', color: 'bg-sky-600' },
  { value: 'confidential', label: 'Confidential', description: 'Known to safety team only', icon: '🔒', color: 'bg-amber-600' },
  { value: 'anonymous', label: 'Anonymous', description: 'Identity hidden completely', icon: '🎭', color: 'bg-slate-600' },
];

export default function MobileHazardReport({ onClose, onSuccess }) {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const { language, isRTL } = useLanguage();
  const { isOnline, saveIncidentOffline, isInitialized } = useOffline();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [locationStatus, setLocationStatus] = useState('idle'); // idle, loading, success, error

  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    location: '',
    occurred_at: new Date().toISOString().slice(0, 16),
    severity: 'minor',
    likelihood: 'remote',
    is_anonymous: false,
    reporting_mode: 'normal',
    // Photo evidence (works offline — base64 stored in IndexedDB queue)
    attachments: [],
    // Per-photo GPS metadata, parallel to attachments[]
    attachment_geo: [],
  });

  // Try to get location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      setLocationStatus('loading');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            location: `Lat: ${position.coords.latitude.toFixed(4)}, Lon: ${position.coords.longitude.toFixed(4)}`
          }));
          setLocationStatus('success');
        },
        () => {
          setLocationStatus('error');
        },
        { timeout: 5000 }
      );
    }
  }, []);

  const progress = (currentStep / STEPS.length) * 100;

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.category !== '';
      case 2: return formData.title.trim() !== '' && formData.description.trim() !== '';
      case 3: return formData.severity !== '';
      case 4: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length && canProceed()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Map frontend fields to backend expected fields
      const incidentData = {
        title: formData.title,
        description: formData.description,
        location: formData.location || 'Not specified',
        occurrence_date: new Date(formData.occurred_at).toISOString(),
        occurrence_category: formData.category,
        is_anonymous: formData.is_anonymous,
        severity: formData.severity,
        likelihood: formData.likelihood,
        reporting_mode: formData.reporting_mode,
        attachments: formData.attachments || [],
        attachment_geo: formData.attachment_geo || []
      };
      
      if (isOnline) {
        // Online: Submit directly to server
        await createIncident(api, incidentData);
        toast.success('Hazard report submitted successfully!');
      } else {
        // Offline: Save locally for later sync
        if (isInitialized) {
          await saveIncidentOffline(incidentData);
          toast.success(
            <div className="flex items-center gap-2">
              <CloudOff className="h-4 w-4" />
              <span>Report saved offline. Will sync when online.</span>
            </div>
          );
        } else {
          toast.error('Offline storage not available. Please try again when online.');
          return;
        }
      }
      onSuccess?.();
    } catch (error) {
      console.error('Failed to submit report:', error);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCategory = QUICK_CATEGORIES.find(c => c.value === formData.category);
  const selectedSeverity = SEVERITY_OPTIONS.find(s => s.value === formData.severity);

  return (
    <div className="fixed inset-0 bg-slate-950 z-50 flex flex-col" data-testid="mobile-hazard-report">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 p-4 safe-area-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-600/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Quick Hazard Report</h1>
              <p className="text-xs text-slate-400">Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1].title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Offline Status Indicator */}
            {!isOnline && (
              <Badge variant="outline" className="flex items-center gap-1 bg-amber-900/30 border-amber-600 text-amber-400">
                <WifiOff className="h-3 w-3" />
                Offline
              </Badge>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <Progress value={progress} className="h-2 bg-slate-800" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-24">
        {/* Step 1: Category Selection */}
        {currentStep === 1 && (
          <div className="space-y-4" data-testid="step-category">
            <h2 className="text-xl font-semibold text-white mb-2">What type of hazard?</h2>
            <p className="text-slate-400 text-sm mb-4">Select the category that best describes the event</p>
            
            <div className="grid grid-cols-2 gap-3">
              {QUICK_CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setFormData({ ...formData, category: cat.value })}
                  className={cn(
                    "p-4 rounded-xl border-2 text-left transition-all active:scale-95",
                    formData.category === cat.value
                      ? "bg-sky-600/20 border-sky-500 text-white"
                      : "bg-slate-900/50 border-slate-700 text-slate-300 hover:border-slate-600"
                  )}
                  data-testid={`category-${cat.value}`}
                >
                  <span className="text-2xl mb-2 block">{cat.icon}</span>
                  <span className="font-medium text-sm">{cat.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {currentStep === 2 && (
          <div className="space-y-4" data-testid="step-details">
            <h2 className="text-xl font-semibold text-white mb-2">Describe the event</h2>
            <p className="text-slate-400 text-sm mb-4">Provide key details about what happened</p>
            
            <div className="space-y-4">
              {/* Reporting Mode Selector */}
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Reporting Mode</Label>
                <div className="grid grid-cols-3 gap-2">
                  {REPORTING_MODES.map(mode => (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setFormData({ 
                        ...formData, 
                        reporting_mode: mode.value,
                        is_anonymous: mode.value === 'anonymous'
                      })}
                      className={cn(
                        "p-3 rounded-lg border transition-all text-center",
                        formData.reporting_mode === mode.value
                          ? `${mode.color} border-transparent text-white`
                          : "bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-500"
                      )}
                      data-testid={`mode-${mode.value}`}
                    >
                      <span className="text-2xl mb-1 block">{mode.icon}</span>
                      <span className="text-xs font-medium">{mode.label}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1 text-center">
                  {REPORTING_MODES.find(m => m.value === formData.reporting_mode)?.description}
                </p>
              </div>

              <div>
                <Label className="text-slate-300 text-sm">Brief Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Bird strike on approach to RWY 27"
                  className="bg-slate-900 border-slate-700 mt-2 h-12 text-base"
                  data-testid="title-input"
                />
              </div>

              <div>
                <Label className="text-slate-300 text-sm">What happened? *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the event in detail. Include what you observed, actions taken, and any contributing factors..."
                  rows={5}
                  className="bg-slate-900 border-slate-700 mt-2 text-base"
                  data-testid="description-input"
                />
              </div>

              <div>
                <Label className="text-slate-300 text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> When did it occur?
                </Label>
                <Input
                  type="datetime-local"
                  value={formData.occurred_at}
                  onChange={(e) => setFormData({ ...formData, occurred_at: e.target.value })}
                  className="bg-slate-900 border-slate-700 mt-2 h-12 text-base"
                  data-testid="datetime-input"
                />
              </div>

              <div>
                <Label className="text-slate-300 text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Location
                  {locationStatus === 'loading' && <span className="text-xs text-sky-400">(detecting...)</span>}
                  {locationStatus === 'success' && <span className="text-xs text-emerald-400">✓ detected</span>}
                </Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Runway 27L, Gate B5, Taxiway Alpha"
                  className="bg-slate-900 border-slate-700 mt-2 h-12 text-base"
                  data-testid="location-input"
                />
              </div>

              {/* Photo evidence — works offline (compressed + queued in IndexedDB) */}
              <div data-testid="hazard-photos-section">
                <Label className="text-slate-300 text-sm flex items-center gap-2">
                  <Camera className="w-4 h-4" /> Photo Evidence
                  <span className="text-xs text-slate-500">(optional, max 5)</span>
                </Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">
                  Snap a photo of the scene — saved locally with GPS, syncs automatically.
                </p>
                <PhotoCapture
                  value={formData.attachments || []}
                  onChange={(attachments) => setFormData({ ...formData, attachments })}
                  geoTag={true}
                  geoValue={formData.attachment_geo || []}
                  onGeoChange={(attachment_geo) => setFormData({ ...formData, attachment_geo })}
                  testId="hazard-photos"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Severity */}
        {currentStep === 3 && (
          <div className="space-y-4" data-testid="step-severity">
            <h2 className="text-xl font-semibold text-white mb-2">How serious was it?</h2>
            <p className="text-slate-400 text-sm mb-4">Assess the potential safety impact</p>
            
            <div className="space-y-3">
              {SEVERITY_OPTIONS.map((sev) => (
                <button
                  key={sev.value}
                  onClick={() => setFormData({ ...formData, severity: sev.value })}
                  className={cn(
                    "w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 active:scale-98",
                    formData.severity === sev.value
                      ? "bg-slate-800 border-sky-500"
                      : "bg-slate-900/50 border-slate-700 hover:border-slate-600"
                  )}
                  data-testid={`severity-${sev.value}`}
                >
                  <div className={cn("w-4 h-4 rounded-full", sev.color)} />
                  <div className="flex-1">
                    <p className="font-medium text-white">{sev.label}</p>
                    <p className="text-sm text-slate-400">{sev.description}</p>
                  </div>
                  {formData.severity === sev.value && (
                    <CheckCircle className="w-5 h-5 text-sky-400" />
                  )}
                </button>
              ))}
            </div>

            {/* Anonymous Option */}
            <div className="mt-6 p-4 bg-slate-900/50 rounded-xl border border-slate-700">
              <button
                onClick={() => setFormData({ ...formData, is_anonymous: !formData.is_anonymous })}
                className="w-full flex items-center gap-3"
              >
                <div className={cn(
                  "w-6 h-6 rounded border-2 flex items-center justify-center transition-colors",
                  formData.is_anonymous ? "bg-sky-600 border-sky-600" : "border-slate-600"
                )}>
                  {formData.is_anonymous && <CheckCircle className="w-4 h-4 text-white" />}
                </div>
                <div className="text-left">
                  <p className="text-white font-medium">Submit Anonymously</p>
                  <p className="text-xs text-slate-400">Your identity will not be recorded</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {currentStep === 4 && (
          <div className="space-y-4" data-testid="step-review">
            <h2 className="text-xl font-semibold text-white mb-2">Review Your Report</h2>
            <p className="text-slate-400 text-sm mb-4">Please confirm the details are correct</p>
            
            <Card className="bg-slate-900/50 border-slate-700">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-3 pb-3 border-b border-slate-700">
                  <span className="text-2xl">{selectedCategory?.icon}</span>
                  <div>
                    <p className="text-xs text-slate-500">Category</p>
                    <p className="text-white font-medium">{selectedCategory?.label}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Title</p>
                  <p className="text-white">{formData.title}</p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Description</p>
                  <p className="text-slate-300 text-sm">{formData.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Date/Time</p>
                    <p className="text-white text-sm">{format(new Date(formData.occurred_at), 'PPp')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Location</p>
                    <p className="text-white text-sm">{formData.location || 'Not specified'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-3 border-t border-slate-700">
                  <div className={cn("w-4 h-4 rounded-full", selectedSeverity?.color)} />
                  <div>
                    <p className="text-xs text-slate-500">Severity Assessment</p>
                    <p className="text-white font-medium">{selectedSeverity?.label}</p>
                  </div>
                </div>

                {formData.is_anonymous && (
                  <div className="flex items-center gap-2 p-2 bg-slate-800 rounded-lg">
                    <Shield className="w-4 h-4 text-sky-400" />
                    <span className="text-sm text-slate-300">Submitting anonymously</span>
                  </div>
                )}

                {formData.attachments && formData.attachments.length > 0 && (
                  <div className="pt-3 border-t border-slate-700" data-testid="hazard-photos-review">
                    <div className="flex items-center gap-2 mb-2">
                      <Camera className="w-4 h-4 text-sky-400" />
                      <p className="text-xs text-slate-500">
                        Photo Evidence ({formData.attachments.length})
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {formData.attachments.map((src, i) => (
                        <img
                          key={i}
                          src={src}
                          alt=""
                          className="w-full h-20 object-cover rounded-md border border-slate-700"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Offline Submission Notice */}
            {!isOnline ? (
              <div className="p-4 bg-amber-950/30 rounded-xl border border-amber-900/50">
                <div className="flex items-start gap-3">
                  <CloudOff className="w-5 h-5 text-amber-400 mt-0.5" />
                  <div>
                    <p className="text-amber-400 font-medium">Offline Mode</p>
                    <p className="text-sm text-slate-400">Report will be saved locally and synced automatically when back online</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-emerald-950/30 rounded-xl border border-emerald-900/50">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <div>
                    <p className="text-emerald-400 font-medium">Ready to Submit</p>
                    <p className="text-sm text-slate-400">Your report will be reviewed by the safety team</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 p-4 safe-area-bottom">
        <div className="flex gap-3">
          {currentStep > 1 && (
            <Button
              variant="outline"
              onClick={handleBack}
              className="flex-1 h-14 border-slate-700 text-slate-300"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          )}
          
          {currentStep < STEPS.length ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className={cn(
                "flex-1 h-14 text-lg",
                currentStep === 1 ? "w-full" : "",
                canProceed() ? "bg-sky-600 hover:bg-sky-700" : "bg-slate-700"
              )}
              data-testid="next-btn"
            >
              Next
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-btn"
            >
              {submitting ? (
                <>
                  <div className="spinner w-5 h-5 mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Submit Report
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
